import {
  ALUMNI,
  FACULTY,
  GMAIL_URL,
  IDEA_TITLE_LIMIT,
  SPONSOR,
  STAFF,
} from '../constants/Constants';
import { Link } from 'react-router-dom';
import Faculty from '../assets/faculty.png';
import Staff from '../assets/staff.png';
import Sponsor from '../assets/sponsor.png';
import Alumni from '../assets/alumni.png';
import Text from '../components/common/Text';
import { Partnership } from '../types/types';

export const validatePassword = async (
  password: string,
): Promise<string | null> => {
  const minLength = 8;
  const hasNumber = /[0-9]/;
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/;
  const hasUpperCase = /[A-Z]/;
  const hasLowerCase = /[a-z]/;

  let isValid = false;
  if (password.length < minLength) isValid = true;
  if (!hasNumber.test(password)) isValid = true;
  if (!hasSpecialChar.test(password)) isValid = true;
  if (!hasUpperCase.test(password)) isValid = true;
  if (!hasLowerCase.test(password)) isValid = true;
  if (isValid)
    return 'Min 8 characters and include 1 number, 1 special character, and 1 uppercase and 1 lowercase';
  else return null;
};

export const formatDate = (dateString: string): string => {
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

export const validateEmail = (email: string) => {
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(String(email).toLowerCase());
};

export const validateName = (value: string) => {
  // Use a regex to allow only letters (a-z, A-Z) and spaces
  return /^[a-zA-Z\s]*$/.test(value);
};

export const ErrorMessage = ({ message }: { message: string }) => {
  return (
    <div>
      {message.includes('Your password is incorrect') ? (
        <>
          {message}{' '}
          <Link to="/request-invite" className="signin-auth-link">
            request an invitation.
          </Link>
        </>
      ) : message.includes('An account with this email does not exist') ? (
        <>
          {message}{' '}
          <Link to="/request-invite" className="signin-auth-link">
            request an invitation.
          </Link>
        </>
      ) : message.includes('already exists.') ? (
        <>
          {
            'An account with this email already exists. Enter a different email or'
          }{' '}
          <Link to="/signin" className="signin-auth-link">
            Sign In.
          </Link>
        </>
      ) : (
        message
      )}
    </div>
  );
};

export const getUserTypeLogo = (type: number) => {
  return (
    <>
      {type === ALUMNI ? (
        <div className="user-type-logo-container">
          <img src={Alumni} alt={Alumni} className="user-type-new-logo" />
          <Text>Alumni</Text>
        </div>
      ) : type === FACULTY ? (
        <div className="user-type-logo-container">
          <img src={Faculty} alt={Faculty} className="user-type-new-logo" />
          <Text>Faculty</Text>
        </div>
      ) : type === SPONSOR ? (
        <div className="user-type-logo-container">
          <img src={Sponsor} alt={Sponsor} className="user-type-new-logo" />
          <Text>Sponsors</Text>
        </div>
      ) : type === STAFF ? (
        <div className="user-type-logo-container">
          <img src={Staff} alt={Staff} className="user-type-new-logo" />
          <Text>Staff</Text>
        </div>
      ) : (
        <div className="user-type">No logo</div>
      )}
    </>
  );
};

export const truncateText = (text: string): string => {
  return text.length > IDEA_TITLE_LIMIT
    ? `${text.slice(0, IDEA_TITLE_LIMIT)}...`
    : text;
};

export const getGmailUrl = (
  email: string,
  subject: string | null,
  body: string | null,
  bcc: string | null,
) => {
  const userAgent = navigator.userAgent;

  const isIOS = /iPad|iPhone|iPod/.test(userAgent);
  const isMobile = /Mobi|Android/i.test(userAgent);

  const clearInstructions =
    'Please replace or remove the previous signature:\n\n';
  const mainBody = body ? `${body}` : clearInstructions;
  if (isIOS || isMobile) {
    const mailtoParams = [];

    mailtoParams.push(`to=${encodeURIComponent(email)}`);

    if (subject) {
      mailtoParams.push(`subject=${encodeURIComponent(subject)}`);
    }

    if (body) {
      mailtoParams.push(`body=${encodeURIComponent(mainBody)}`);
    }

    if (bcc) {
      mailtoParams.push(`bcc=${encodeURIComponent(bcc)}`);
    }
    return `mailto:?${mailtoParams.join('&')}`;
  } else {
    const params = new URLSearchParams({
      view: 'cm',
      fs: '1',
      to: email,
      tf: '1',
    });

    if (body) {
      params.append('body', body);
    }

    if (bcc) {
      params.append('bcc', bcc);
    }
    if (subject) {
      params.append('su', subject);
    }
    return `${GMAIL_URL}/?${params.toString()}`;
  }
};

export const validateLinkedInURL = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return (
      parsedUrl.protocol === 'https:' &&
      parsedUrl.hostname === 'www.linkedin.com' &&
      /^\/in\/[A-Za-z0-9_-]+\/?$/.test(parsedUrl.pathname)
    );
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const validateGoogleScholarURL = (url: string): boolean => {
  try {
    const parsedUrl = new URL(url);
    return (
      parsedUrl.protocol === 'https:' &&
      parsedUrl.hostname === 'scholar.google.com' &&
      /^\/citations\?user=[A-Za-z0-9\-_.!~*'()$&+,;=:@]+$/.test(
        parsedUrl.pathname + parsedUrl.search,
      )
    );
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const timeAgo = (date: string): string => {
  const now: Date = new Date();
  const pastDate: Date = new Date(date);

  if (isNaN(pastDate.getTime())) {
    return 'Invalid date';
  }

  const seconds: number = Math.floor(
    (now.getTime() - pastDate.getTime()) / 1000,
  );
  const days: number = Math.floor(seconds / 86400);

  if (days > 0) {
    return `Posted ${days}d ago`;
  } else {
    return 'Posted today';
  }
};

export const validatePatent = (value: string): boolean => {
  const pattern = /^[0-9A-Z/.,\s-]*[A-Z0-9]+[0-9A-Z/.,\s-]*$/;
  return pattern.test(value);
};

export const getSupportNames = (
  supportIds: number[],
  seekings: Partnership[],
) => {
  return supportIds
    .map((id) => {
      const support = seekings.find((option) => option.id === id);
      return support ? support.name : '';
    })
    .filter((name) => name !== '');
};

export const replaceNewlinesWithBr = (text: string): string => {
  return text.replace(/\n/g, '<br>');
};
